/**
 * usePageHeartbeat — safe, throttled, admin-analytics-facing page ping.
 *
 * Mounts once at the top of the tree (in App.js). When the user is
 * authenticated, maps the current route to a friendly page label via a
 * fixed whitelist and POSTs `{page_label}` to `/api/me/heartbeat` at
 * most every 60 seconds. Route change + interval both trigger a check,
 * but the throttle floor is respected in either case.
 *
 * SAFETY RULES
 *   • Never sends `window.location.href`, query strings, hashes, or
 *     dynamic IDs — mapping is pathname-only through a whitelist.
 *   • Anything unrecognised maps to "Unknown page" (the server also
 *     enforces this via `core/page_labels.py`).
 *   • Never sends form values, keystrokes, or click data.
 *   • Skips heartbeat entirely when the user is not authenticated.
 *   • Uses `credentials: 'include'` so the existing cookie/JWT auth
 *     works without adding anything new.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Minimum seconds between heartbeats. Kept in one place so it's easy
// to tune later without hunting through the effect.
const MIN_INTERVAL_MS = 60 * 1000;

// Ordered rule list. Each rule is a `[test, label]` pair. First match
// wins. Tests can be a string (exact-match), or a regex. Query strings
// and hashes are stripped before matching — a rule NEVER sees them.
//
// Anything that doesn't match falls through to "Unknown page" so we
// never accidentally send a private path. IDs never appear in labels.
const RULES = [
  // Direct exact-matches
  ['/', 'Home'],
  ['/dashboard', 'Dashboard'],
  ['/pricing', 'Pricing'],
  ['/support', 'Support'],
  ['/messages', 'Messages'],
  ['/notifications', 'Notifications'],
  ['/saved', 'Saved'],
  ['/news', 'News'],
  ['/events', 'Events'],
  ['/casting-calls', 'Casting Calls'],
  ['/casting-calls/new', 'Casting Calls'],
  ['/talent', 'Talent Directory'],
  ['/agents', 'Agents Directory'],
  ['/casting-professionals', 'Casting Professionals Directory'],
  ['/companies', 'Companies Directory'],
  ['/services', 'Services Directory'],
  ['/crew', 'Crew Directory'],
  ['/audition-rooms', 'Audition Rooms'],
  ['/profile', 'Profile'],
  ['/profile/edit', 'Profile Editor'],
  ['/account/settings', 'Settings'],
  ['/account/subscription', 'Account & Billing'],
  ['/account/membership', 'Account & Billing'],

  // Regex rules for detail-pages — IDs never enter the label
  [/^\/casting-calls\/[^/]+\/?$/, 'Casting Call Detail'],
  [/^\/audition-rooms\/[^/]+\/?$/, 'Audition Room Detail'],
  [/^\/talent\/[^/]+\/?$/, 'Talent Directory'],
  [/^\/agents\/[^/]+\/?$/, 'Agents Directory'],
  [/^\/companies\/[^/]+\/?$/, 'Companies Directory'],
  [/^\/services\/[^/]+\/?$/, 'Services Directory'],
  [/^\/casting-professionals\/[^/]+\/?$/, 'Casting Professionals Directory'],
  [/^\/crew\/[^/]+\/?$/, 'Crew Directory'],
  [/^\/news\/[^/]+\/?$/, 'News'],
  [/^\/events\/[^/]+\/?$/, 'Events'],
  [/^\/messages\/[^/]+\/?$/, 'Messages'],
  [/^\/notifications\/[^/]+\/?$/, 'Notifications'],
];

// Query string → dashboard tab → admin page label. Only checked when
// pathname === '/dashboard'. Anything unknown falls through to
// "Dashboard".
const DASHBOARD_TAB_ADMIN_LABELS = {
  overview: 'Admin Dashboard',
  planner: 'Admin Planner',
  analytics: 'Admin Analytics',
  users: 'Admin Users',
  approvals: 'Admin Approvals',
  archived: 'Admin Archived Users',
  'listing-claims': 'Admin Listing Claims',
  'messenger-moderation': 'Admin Messenger Moderation',
  'audition-moderation': 'Admin Audition Moderation',
  content: 'Admin Content',
  team: 'Admin Team',
  support: 'Admin Support',
  status: 'Admin Status',
  cancellations: 'Admin Cancellations',
  maintenance: 'Admin Maintenance',
  settings: 'Admin Settings',
  notifications: 'Admin Notifications',
};

// Query string → dashboard tab → regular user page label.
const DASHBOARD_TAB_USER_LABELS = {
  overview: 'Dashboard',
  profile: 'Profile Editor',
  applications: 'Applications',
  planner: 'Planner',
  projects: 'Projects',
  saved: 'Saved',
  connections: 'Network',
  network: 'Network',
  messages: 'Messages',
  notifications: 'Notifications',
  events: 'Events',
  settings: 'Settings',
  billing: 'Account & Billing',
  membership: 'Account & Billing',
  subscription: 'Account & Billing',
  support: 'Support',
};

/**
 * Map a pathname+search into a safe whitelisted label. IDs and query
 * strings NEVER appear in the returned string.
 */
export function routeToPageLabel(pathname, search, activeRole) {
  const clean = (pathname || '/').split('#')[0].replace(/\/+$/, '') || '/';

  // Dashboard is special: the tab query decides which admin/user page
  // we're actually on. Strip everything else from the search string.
  if (clean === '/dashboard') {
    try {
      const params = new URLSearchParams(search || '');
      const tab = (params.get('tab') || 'overview').toLowerCase();
      if (activeRole === 'admin' && DASHBOARD_TAB_ADMIN_LABELS[tab]) {
        return DASHBOARD_TAB_ADMIN_LABELS[tab];
      }
      if (DASHBOARD_TAB_USER_LABELS[tab]) {
        return DASHBOARD_TAB_USER_LABELS[tab];
      }
    } catch {
      /* fall through */
    }
    return activeRole === 'admin' ? 'Admin Dashboard' : 'Dashboard';
  }

  for (const [test, label] of RULES) {
    if (typeof test === 'string') {
      if (clean === test) return label;
    } else if (test instanceof RegExp) {
      if (test.test(clean)) return label;
    }
  }
  return 'Unknown page';
}

export function usePageHeartbeat() {
  const { user } = useAuth();
  const location = useLocation();
  const lastSentAtRef = useRef(0);
  const lastLabelRef = useRef(null);

  useEffect(() => {
    if (!user) return undefined;

    const sendHeartbeat = async () => {
      const label = routeToPageLabel(location.pathname, location.search, user?.active_role);
      const now = Date.now();
      // Throttle: never fire more than once per MIN_INTERVAL_MS unless
      // the label changed (route change should surface quickly). Even
      // on label change, keep a 5s floor so rapid nav does not spam.
      const isRouteChange = lastLabelRef.current !== label;
      const minFloor = isRouteChange ? 5000 : MIN_INTERVAL_MS;
      if (now - lastSentAtRef.current < minFloor) return;

      try {
        await fetch(`${API_BASE}/api/me/heartbeat`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ page_label: label }),
        });
        lastSentAtRef.current = now;
        lastLabelRef.current = label;
      } catch {
        // Silent — a missed heartbeat is not an error the user should
        // ever see. Server-side `last_active_at` is still stamped by
        // any authenticated request the user makes.
      }
    };

    // Initial ping (respecting the 5s floor if any) + interval.
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, MIN_INTERVAL_MS);
    return () => clearInterval(interval);
    // Route change should re-run the effect so `sendHeartbeat` sees the
    // updated pathname/search.
  }, [user, location.pathname, location.search]);
}
